import AutoCompleteFormField from '@acorn-portal/shared/src/components/inputs/AutoCompleteFormField';
import RadioGroupButton from '@acorn-portal/shared/src/components/inputs/radio-button-group';
import SelectFormField, { IOptions } from '@acorn-portal/shared/src/components/inputs/select';
import ToggleButtonFormField from '@acorn-portal/shared/src/components/inputs/toggle-button-form-field';
import { useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { isEmployed, isStudent } from '@acorn-portal/shared/src/utils';
import { ADDITIONAL_DRIVER_ERRORS } from '@acorn-portal/shared/src/utils/constants/ErrorMessages';
import { DriverShortFormValues } from '@acorn-portal/shared/src/utils/initialValues/get-driver-short-form-values';
import { TAXI_VEHICLE_USE } from '@acorn-portal/shared/src/utils/modalText';
import { useVehicleUsageOptions } from '@acorn-portal/shared/src/utils/taxiVehicleUsageOptions';
import { Box, Button, Collapse, Divider } from '@mui/material';
import { Form, Formik, FormikConfig, FormikHelpers } from 'formik';
import { FC } from 'react';
import { DriverDetailsFormItem } from '../../manage-drivers/types';
import { ExtraDriverField } from '../types';
import { FormikLogErrors } from '@acorn-portal/shared/src/pages/mta/components/FormikLogErrors';

export interface DriverShortFormProps {
	initialValues: DriverShortFormValues;
	driverDetails: DriverDetailsFormItem;
	schema: FormikConfig<DriverShortFormValues>['validationSchema'];
	extraFields?: ExtraDriverField[];
	onCancel: () => void;
	onSubmit: (formValues: DriverShortFormValues, driverDetails?: DriverDetailsFormItem) => void;
	submitText?: string;
}

// This is typically for the proposer driver, but can be used for any driver where only the employment details are required
const DriverShortForm: FC<DriverShortFormProps> = ({
	initialValues,
	driverDetails,
	onCancel,
	onSubmit,
	schema,
	extraFields = [],
	submitText = 'Save',
}) => {
	// Dropdown Usage Options (if additional field present)
	const lists = useAppSelector((redux) => redux.referenceData);
	const vehicleUsageOptions = useVehicleUsageOptions();
	// Label Wording
	const isProposer = driverDetails.relationshipToProposer === 'P';
	const pronoun = isProposer ? 'you' : 'they';
	const possessivePronoun = isProposer ? 'your' : 'their';

	const handleSubmit = (values: DriverShortFormValues, formikHelpers: FormikHelpers<DriverShortFormValues>) => {
		onSubmit(values, driverDetails);
		formikHelpers.resetForm();
		window.scrollTo({
			top: 100,
			left: 100,
			behavior: 'smooth',
		});
	};

	return (
		<Formik<DriverShortFormValues> initialValues={initialValues} validationSchema={schema} onSubmit={handleSubmit}>
			{(formik) => {
				const resetField = (fieldName: keyof DriverShortFormValues) => {
					formik.setFieldValue(fieldName, '');
					formik.setFieldTouched(fieldName, false);
					formik.setFieldError(fieldName, undefined);
				};
				return (
					<Box
						sx={(theme) => ({
							py: 2,
							px: 1,
							border: `1px solid ${theme.palette.divider}`,
							borderRadius: 2,
						})}
					>
						<Form>
							<FormikLogErrors />
							<ToggleButtonFormField
								label={`What is ${possessivePronoun} employment status?`}
								name='employmentType'
								id='employmentType'
								value={formik.values.employmentType}
								onChange={(value) => {
									formik.setFieldValue('employmentType', value);

									switch (value) {
										case 'F': {
											resetField('occupation');
											break;
										}
										case 'S':
										case 'E': {
											resetField('occupation');
											resetField('industry');

											break;
										}
										default:
											break;
									}
								}}
								options={lists.employmentStatusTypes as IOptions[]}
								error={formik.touched.employmentType && Boolean(formik.errors.employmentType)}
								helperText={formik.touched.employmentType && formik.errors.employmentType}
							/>
							<RadioGroupButton
								label='Is this occupation full time?'
								name='employmentStatus'
								id='employmentStatus'
								value={formik.values.employmentStatus}
								onChange={(value) => {
									formik.setFieldError('employmentStatus', '');
									formik.setFieldValue('employmentStatus', value, false);
								}}
								error={formik.touched.employmentStatus && Boolean(formik.errors.employmentStatus)}
								helperText={formik.touched.employmentStatus && formik.errors.employmentStatus}
							/>
							<Collapse in={isStudent(formik.values.employmentType)}>
								{isStudent(formik.values.employmentType) && (
									<AutoCompleteFormField
										label={`What is ${possessivePronoun} education type?`}
										name='occupation'
										id='occupation'
										value={formik.values.occupation}
										onChange={(value) => formik.setFieldValue('occupation', value, true)}
										options={lists.studentOccupationTypes}
										error={formik.touched.occupation && Boolean(formik.errors.occupation)}
										helperText={formik.touched.occupation && formik.errors.occupation}
									/>
								)}
							</Collapse>

							<Collapse in={isEmployed(formik.values.employmentType)}>
								{isEmployed(formik.values.employmentType) && (
									<>
										<AutoCompleteFormField
											label={`What is ${possessivePronoun} occupation?`}
											name='occupation'
											id='occupation'
											value={formik.values.occupation}
											onChange={(value) => formik.setFieldValue('occupation', value)}
											options={lists.occupationTypes}
											error={formik.touched.occupation && Boolean(formik.errors.occupation)}
											helperText={formik.touched.occupation && formik.errors.occupation}
										/>

										<AutoCompleteFormField
											label='What type of industry is this?'
											name='industry'
											id='industry'
											value={formik.values.industry}
											onChange={(value) => formik.setFieldValue('industry', value)}
											options={lists.industryTypes}
											error={formik.touched.industry && Boolean(formik.errors.industry)}
											helperText={formik.touched.industry && formik.errors.industry}
										/>
										{extraFields.includes('driverUsage') && (
											<SelectFormField
												label={`What will ${pronoun} be using the vehicle for?`}
												name='driverUsage'
												id='driverUsage'
												value={formik.values.driverUsage}
												onChange={(value) => formik.setFieldValue('driverUsage', value)}
												options={vehicleUsageOptions}
												error={formik.touched.driverUsage && Boolean(formik.errors.driverUsage)}
												helperText={
													(formik.touched.driverUsage && formik.errors.driverUsage) ||
													(formik.values.driverUsage === '4' &&
														!driverDetails.isMainDriver &&
														ADDITIONAL_DRIVER_ERRORS.driverUsageSDP)
												}
												modal={{
													title: TAXI_VEHICLE_USE.header,
													description: TAXI_VEHICLE_USE.text,
												}}
											/>
										)}
									</>
								)}
							</Collapse>

							<RadioGroupButton
								label={`Do ${pronoun} have another occupation?`}
								name='otherEmployment'
								id='otherEmployment'
								value={formik.values.otherEmployment}
								onChange={(value) => {
									formik.setFieldValue('otherEmployment', value);
									formik.setFieldValue('otherEmploymentStatus', '');
									formik.setFieldValue('otherOccupation', '');
									formik.setFieldValue('otherIndustry', '');
									formik.setFieldValue('otherEmploymentType', '');
								}}
								error={formik.touched.otherEmployment && Boolean(formik.errors.otherEmployment)}
								helperText={formik.touched.otherEmployment && formik.errors.otherEmployment}
							/>
							<Collapse in={formik.values.otherEmployment === 'yes'}>
								<Divider sx={{ mb: 4 }} />
								{formik.values.otherEmployment === 'yes' && (
									<>
										<ToggleButtonFormField
											label={`What is ${possessivePronoun} employment status?`}
											name='otherEmploymentType'
											id='otherEmploymentType'
											value={formik.values.otherEmploymentType}
											onChange={(value) => {
												formik.setFieldError('otherEmploymentStatus', '');
												formik.setFieldValue('otherEmploymentType', value);
											}}
											options={lists.employmentStatusTypes as IOptions[]}
											error={formik.touched.otherEmploymentType && Boolean(formik.errors.otherEmploymentType)}
											helperText={formik.touched.otherEmploymentType && formik.errors.otherEmploymentType}
										/>
										<RadioGroupButton
											label='Is this occupation full time?'
											name='otherEmploymentStatus'
											id='otherEmploymentStatus'
											value={formik.values.otherEmploymentStatus}
											onChange={(value) => {
												formik.setFieldValue('otherEmploymentStatus', value, false);
											}}
											error={formik.touched.otherEmploymentStatus && Boolean(formik.errors.otherEmploymentStatus)}
											helperText={formik.touched.otherEmploymentStatus && formik.errors.otherEmploymentStatus}
										/>
										<Collapse in={isStudent(formik.values.otherEmploymentType)}>
											{isStudent(formik.values.otherEmploymentType) && (
												<AutoCompleteFormField
													label={`What is ${possessivePronoun} education type?`}
													name='otherOccupation'
													id='otherOccupation'
													value={formik.values.otherOccupation}
													onChange={(value) => formik.setFieldValue('otherOccupation', value)}
													options={lists.studentOccupationTypes}
													error={formik.touched.otherOccupation && Boolean(formik.errors.otherOccupation)}
													helperText={formik.touched.otherOccupation && formik.errors.otherOccupation}
												/>
											)}
										</Collapse>

										<Collapse in={isEmployed(formik.values.otherEmploymentType)}>
											{isEmployed(formik.values.otherEmploymentType) && (
												<>
													<AutoCompleteFormField
														label={`What is ${possessivePronoun} occupation?`}
														name='otherOccupation'
														id='otherOccupation'
														value={formik.values.otherOccupation}
														onChange={(value) => formik.setFieldValue('otherOccupation', value)}
														options={lists.occupationTypes}
														error={formik.touched.otherOccupation && Boolean(formik.errors.otherOccupation)}
														helperText={formik.touched.otherOccupation && formik.errors.otherOccupation}
													/>

													<AutoCompleteFormField
														label='What type of industry is this?'
														name='otherIndustry'
														id='otherIndustry'
														value={formik.values.otherIndustry}
														onChange={(value) => formik.setFieldValue('otherIndustry', value)}
														options={lists.industryTypes}
														error={formik.touched.otherIndustry && Boolean(formik.errors.otherIndustry)}
														helperText={formik.touched.otherIndustry && formik.errors.otherIndustry}
													/>
												</>
											)}
										</Collapse>
									</>
								)}
							</Collapse>
							<Box
								display='flex'
								justifyContent='flex-end'
								sx={(theme) => ({
									px: 2,
									[theme.breakpoints.down('md')]: {
										pr: 'unset',
									},
								})}
							>
								<Button
									sx={(theme) => ({
										my: 2,
										m: 'auto',
										minWidth: '10%',
										[theme.breakpoints.down('md')]: {
											width: '50%',
										},
									})}
									variant='text'
									color='secondary'
									onClick={() => {
										onCancel();
										formik.resetForm();
										window.scrollTo({
											top: 100,
											left: 100,
											behavior: 'smooth',
										});
									}}
								>
									Cancel
								</Button>
								<Button
									sx={(theme) => ({
										my: 2,
										minWidth: '100px',
										ml: 'auto',
										[theme.breakpoints.down('md')]: {
											width: '50%',
										},
									})}
									variant='contained'
									color='primary'
									type='submit'
								>
									{submitText}
								</Button>
							</Box>
						</Form>
					</Box>
				);
			}}
		</Formik>
	);
};

export default DriverShortForm;
